import {
  ArrowBigUp,
  AudioLines,
  Check,
  Copy,
  Download,
  ExternalLink,
  Eye,
  Flame,
  Forward,
  Heart,
  Hourglass,
  Images,
  MessageCircle,
  Play,
  Plus,
  Repeat2,
  SearchX,
  Send,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode, type RefObject } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router'
import { isApiError } from '../../api/client'
import { keys, useConfig, usePost, useSendPost, useSources } from '../../api/queries'
import type { LinkedPost, Measurement, MetricState, MetricView, PostDetail } from '../../api/types'
import { downloadFile, openExternal } from '../../lib/download'
import {
  PLATFORM_LABEL,
  formatAge,
  formatCount,
  formatDateTime,
  formatPct,
  formatX,
  handleLabel,
  plural,
  postFileName,
  sourceInput,
  xLevel,
} from '../../lib/format'
import { isInTelegram } from '../../telegram'
import { useViewState } from '../../lib/navigation'
import { evidenceStart } from '../../lib/evidencePlayback'
import { Avatar } from '../Avatar'
import { Button } from '../Button'
import { EmptyState } from '../EmptyState'
import { FavoriteButton } from '../favorites/FavoriteButton'
import { AddToPlanButton } from '../plan/AddToPlanButton'
import { copyOrShow } from '../ManualCopy'
import { Spinner } from '../Spinner'
import { toast } from '../Toast'
import { AddSourceDialog } from '../sources/AddSourceDialog'
import { AddedBlock, isUntracked } from './AddedBlock'
import { Storyboard } from './Storyboard'
import { ReelLanguageField } from './ReelLanguage'
import { ReelTranscript } from './ReelTranscript'
import { isMeta, metricStateLabel, postHref, postMeasurement, primaryViews, viewsLabel } from '../../lib/measurement'
import { MeasurementNote } from '../MeasurementNote'

/** split — медиа слева, сведения справа (компьютер); stack — всё столбиком (телефон). */
export type CardLayout = 'split' | 'stack'

/**
 * Сколько по высоте может занять плеер в столбике: видимая область минус шапка и
 * строка с ♥ и «Скачать» под плеером — они не должны уходить за экран. Страница
 * с нижними вкладками или панелью карусели передаёт свою высоту.
 */
export const MEDIA_MAX_OVERLAY = 'calc(var(--app-height) - var(--safe-top) - var(--safe-bottom) - 3rem - 4.25rem)'

interface PostCardProps {
  id: number
  layout: CardLayout
  videoRef: RefObject<HTMLVideoElement | null>
  extraActions?: ReactNode
  /** Столбик: потолок высоты плеера (CSS). */
  mediaMax?: string
  /** Столбик: сколько сверху закрывают липкие шапки — плеер при перемотке встаёт под них. */
  mediaScrollMargin?: string
}

export function PostCard({ id, layout, videoRef, extraActions, mediaMax, mediaScrollMargin }: PostCardProps) {
  const [params] = useSearchParams()
  const query = usePost(id, postMeasurement(params))

  if (query.isPending) {
    return (
      <div className="flex min-h-64 flex-1 items-center justify-center">
        <Spinner size={24} />
      </div>
    )
  }
  if (query.isError) {
    const missing = isApiError(query.error, 404)
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-6">
        <EmptyState icon={SearchX} title={missing ? 'Пост не найден' : 'Не удалось загрузить пост'}>
          {!missing && (
            <Button size="sm" className="mt-3" onClick={() => void query.refetch()}>
              Повторить
            </Button>
          )}
        </EmptyState>
      </div>
    )
  }

  return (
    <LoadedCard
      key={query.data.id}
      post={query.data}
      layout={layout}
      videoRef={videoRef}
      extraActions={extraActions}
      mediaMax={mediaMax}
      mediaScrollMargin={mediaScrollMargin}
    />
  )
}

function LoadedCard({
  post,
  layout,
  videoRef,
  extraActions,
  mediaMax = MEDIA_MAX_OVERLAY,
  mediaScrollMargin,
}: Omit<PostCardProps, 'id'> & { post: PostDetail }) {
  // Ссылка на ролик протухла — скачивать нечего, кнопку прячем.
  const [videoFailed, setVideoFailed] = useState(false)
  const visual = post.platform === 'instagram'
  const media = <PostMedia post={post} videoRef={videoRef} failed={videoFailed} onFail={() => setVideoFailed(true)} />
  const info = <PostInfo post={post} videoRef={videoRef} extraActions={extraActions} canDownload={!videoFailed} />

  if (!visual) {
    return <div data-view-scroll={`post.${post.id}.info`} className="mx-auto w-full max-w-2xl min-w-0 flex-1 overflow-y-auto">{info}</div>
  }
  if (layout === 'split') {
    return (
      <div className="flex h-full min-h-0 w-full">
        <div className="aspect-[9/16] h-full shrink-0 bg-[var(--cinema)]">{media}</div>
        <div data-view-scroll={`post.${post.id}.info`} className="min-w-0 flex-1 overflow-y-auto">{info}</div>
      </div>
    )
  }
  // Нижняя граница оставляет место плееру на низком экране.
  const max = `max(11rem, ${mediaMax})`
  return (
    <PostInfo
      post={post}
      videoRef={videoRef}
      extraActions={extraActions}
      canDownload={!videoFailed}
      player={
        <div
          data-media
          className="post-player-frame"
          style={{ height: `min(clamp(175px, 66vw, 260px), ${max})`, scrollMarginTop: mediaScrollMargin }}
        >
          {media}
        </div>
      }
    />
  )
}


interface MediaProps {
  post: PostDetail
  videoRef: RefObject<HTMLVideoElement | null>
  failed: boolean
  onFail: () => void
}

function PostMedia({ post, videoRef, failed: videoFailed, onFail }: MediaProps) {
  const location = useLocation()
  const [time, setTime] = useViewState(`post.${post.id}.time`, evidenceStart(location.state, post.id))
  const [thumbFailed, setThumbFailed] = useState(false)
  const [started, setStarted] = useState(false)
  const qc = useQueryClient()
  const showVideo = Boolean(post.video_url) && !videoFailed
  const thumb = post.thumb_url && !thumbFailed ? post.thumb_url : null
  // Ролика не было в кэше, но живая ссылка сработала: сервер скачал его целиком
  // до первого байта. Перечитываем карточку — появятся «Скачать» и раскадровка.
  const onLoaded = () => {
    const video = videoRef.current
    if (video && time > 0 && Number.isFinite(video.duration)) video.currentTime = Math.min(time, video.duration)
    if (!post.video_cached) void qc.invalidateQueries({ queryKey: keys.post(post.id) })
  }

  let note: string | null = null
  if (videoFailed) note = 'Видео недоступно: ссылка Instagram устарела, а в кэш ролик не попал.'
  else if (post.kind === 'carousel')
    note = post.slides_count
      ? `Карусель · ${post.slides_count} ${plural(post.slides_count, 'слайд', 'слайда', 'слайдов')}`
      : 'Карусель'
  else if (post.kind === 'image_post') note = 'Картинка'
  else if (!post.has_video) note = 'Ролика нет в базе'

  return (
    <div className="post-player relative flex size-full items-center justify-center overflow-hidden">
      {showVideo ? (
        <video
          // key: при листании плеер пересоздаётся, а не доигрывает прошлый ролик
          key={post.id}
          ref={videoRef}
          src={post.video_url ?? undefined}
          poster={thumb ?? undefined}
          controls
          playsInline
          preload="metadata"
          onLoadedMetadata={onLoaded}
          onPlay={() => setStarted(true)}
          onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
          onError={onFail}
          className="size-full object-contain"
        />
      ) : thumb ? (
        <img
          key={post.id}
          src={thumb}
          alt=""
          onError={() => setThumbFailed(true)}
          className="size-full object-contain"
        />
      ) : (
        <Images size={36} className="text-white/30" aria-hidden />
      )}
      {showVideo && !started && (
        <button
          type="button"
          aria-label="Воспроизвести видео"
          onClick={() => {
            const video = videoRef.current
            if (!video) return
            video.focus({ preventScroll: true })
            void video.play().catch(() => {})
          }}
          className="absolute top-1/2 left-1/2 grid size-12 -translate-1/2 place-items-center rounded-full border border-white/60 bg-black/55 text-white shadow-md backdrop-blur-sm focus-visible:outline-white"
        >
          <Play size={20} fill="currentColor" aria-hidden />
        </button>
      )}
      {!showVideo && (
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 bg-linear-to-t from-black/85 to-transparent p-4 pt-10 text-center text-sm text-white">
          {note && <p className="text-pretty text-white/85">{note}</p>}
          {post.url && (
            <button
              type="button"
              onClick={() => openExternal(post.url ?? '')}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3 text-sm font-medium text-black hover:bg-white/90"
            >
              <ExternalLink size={15} />
              Открыть в Instagram
            </button>
          )}
        </div>
      )}
    </div>
  )
}


const WHOSE: Record<string, string> = {
  instagram: 'к обычному у автора',
  telegram: 'к обычному у канала — по реакциям',
  reddit: 'к обычному у сабреддита',
}

const OWNER: Record<string, string> = { instagram: 'у автора', telegram: 'у канала', reddit: 'у сабреддита' }

const LEVEL_BOX = {
  x1: 'bg-x1-bg text-fg',
  x2: 'bg-x2-bg text-x2-fg',
  x3: 'bg-x3-bg text-x3-fg',
  x5: 'bg-x5-bg text-x5-fg',
} as const

function PostInfo({
  post,
  videoRef,
  extraActions,
  canDownload,
  player,
}: {
  post: PostDetail
  videoRef: RefObject<HTMLVideoElement | null>
  extraActions?: ReactNode
  canDownload: boolean
  player?: ReactNode
}) {
  const now = Date.now()
  const a = post.author
  const name = a.title ?? handleLabel(post.platform, a.handle)
  const followers =
    a.followers !== null
      ? `${formatCount(a.followers)} ${a.followers >= 1000 ? 'подписчиков' : plural(a.followers, 'подписчик', 'подписчика', 'подписчиков')}`
      : null

  const actions = (
    <>
      <FavoriteButton postId={post.id} />
      {post.download_url && canDownload && <SendToChat postId={post.id} />}
      {post.download_url && canDownload && (
        <Button
          size="icon"
          variant="ghost"
          title="Скачать ролик (D)"
          aria-label="Скачать ролик"
          onClick={() => downloadFile(post.download_url ?? '', postFileName(post))}
        >
          <Download size={18} />
        </Button>
      )}
      {post.url && (
        <Button
          size="icon"
          variant="ghost"
          title={`Открыть в ${PLATFORM_LABEL[post.platform]}`}
          aria-label={`Открыть в ${PLATFORM_LABEL[post.platform]}`}
          onClick={() => openExternal(post.url ?? '')}
        >
          <ExternalLink size={18} />
        </Button>
      )}
      {extraActions}
    </>
  )

  return (
    <div className="flex flex-col gap-4 py-4 desk:p-5">
      <header className="flex items-start gap-3">
        <Avatar name={a.title ?? a.handle} src={null} size={40} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate font-semibold">{name}</div>
          <div className="mt-0.5 truncate text-sm text-muted">
            {[a.title ? handleLabel(post.platform, a.handle) : null, followers, formatAge(post.published_at, now)]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        {!player && <div className="-mt-1 -mr-1.5 flex shrink-0 items-center">{actions}</div>}
      </header>

      {player && (
        <>
          <PostMetricSummary post={post} />
          {player}
          <div className="flex flex-wrap items-center gap-1 rounded-xl bg-card px-2 ring-1 ring-line">
            {actions}
          </div>
        </>
      )}

      <AddToPlanButton post={post} />

      {post.kind === 'reel' && <ReelLanguageField post={post} />}
      {post.platform === 'instagram' && <MeasurementNote value={post} />}

      {/* Автор не отслеживается: ×N нет, вместо блока ×N — доля от подписчиков в AddedBlock. */}
      {!isUntracked(post) && <Hype post={post} />}

      <AddedBlock post={post} />

      {post.repost && <RepostBlock post={post} />}

      <MetricsList post={post} />

      <p className="text-xs text-muted">
        {post.published_at && <>Опубликован {formatDateTime(post.published_at)}</>}
        {post.metrics_at && post.platform !== 'instagram' && <> · цифры на {formatDateTime(post.metrics_at)}</>}
      </p>

      {post.sound && <SoundLine post={post} />}

      {post.platform === 'instagram' ? (
        <Tabs post={post} videoRef={videoRef} />
      ) : (
        <TextBlock title={post.platform === 'reddit' ? 'Заголовок и текст' : 'Текст'} text={post.text ?? post.caption} />
      )}

      {post.reposted_by.length > 0 && (
        <Linked title={post.repost ? 'Ещё репостнули' : 'Репостнули'} posts={post.reposted_by} view={post.metric_view} />
      )}
      {post.siblings.length > 0 && <Linked title="Тот же пост у других" posts={post.siblings} view={post.metric_view} />}
    </div>
  )
}

const SEND_ERRORS: Record<string, string> = {
  bot_blocked: 'Бот не может написать вам: откройте чат с ботом и нажмите «Старт»',
  bot_disabled: 'Бот выключен',
  no_video: 'Ролика ещё нет у нас — запустите его в плеере',
}

/**
 * «Отправить в чат» — в Mini App главное действие с роликом: WebView файл на
 * телефон не сохраняет, а в чате с ботом ролик можно сохранить и переслать.
 */
function SendToChat({ postId }: { postId: number }) {
  const config = useConfig()
  const send = useSendPost()
  if (!isInTelegram() || !config.data?.bot_enabled) return null
  return (
    <Button
      size="icon"
      variant="ghost"
      aria-label="Отправить ролик в чат с ботом"
      disabled={send.isPending}
      onClick={() =>
        send.mutate(postId, {
          onSuccess: (r) =>
            toast(r.sent === 'link' ? 'Файл большой — в чат с ботом ушла ссылка' : 'Отправил в чат с ботом', {
              ms: 3000,
            }),
          onError: (err) =>
            toast((isApiError(err) && err.detail && SEND_ERRORS[err.detail]) || 'Не получилось отправить', {
              error: true,
              ms: 4000,
            }),
        })
      }
    >
      {send.isPending ? <Spinner size={16} /> : <Send size={18} />}
    </Button>
  )
}

function Hype({ post }: { post: PostDetail }) {
  return (
    <div className="flex flex-col gap-2">
      {post.xn !== null ? (
        <div className={`rounded-xl px-4 py-3 ${LEVEL_BOX[xLevel(post.xn)]}`}>
          <div className="flex flex-wrap items-baseline gap-x-2.5">
            <span className="text-4xl leading-tight font-semibold tracking-tight tabular-nums">{formatX(post.xn)}</span>
            <span className="text-sm font-medium">{isMeta(post) && 'Meta · '}{WHOSE[post.platform] ?? WHOSE.instagram}{post.xn_status === 'stale' && ' · устарело'}</span>
          </div>
          {post.is_early && !isMeta(post) && (
            <p className="mt-0.5 flex items-center gap-1.5 text-sm opacity-85">
              <Hourglass size={14} className="shrink-0" aria-hidden />
              Ранняя оценка — пост ещё набирает
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-xl bg-surface px-4 py-3 text-sm text-muted ring-1 ring-line ring-inset">
          {isMeta(post)
            ? post.xn_status === 'warmup'
              ? 'Meta ×N накапливает зрелые данные: нужен замер этого поста позже 48 часов после публикации и минимум 5 зрелых постов того же формата для сравнения.'
              : post.xn_status === 'stale'
                ? 'Свежей оценки Meta пока нет. Сохранённые замеры остаются с исходной датой.'
                : '×N Meta пока недоступен: нет достаточного набора сопоставимых замеров.'
            : post.repost
            ? '×N не считается: оригинала нет среди наших источников. Цифры — у репоста.'
            : '×N ещё не посчитан: оценка появится после ночного пересчёта.'}
        </div>
      )}
      {post.low_data && (
        <div className="flex items-start gap-2 rounded-lg bg-surface px-3 py-2 text-sm ring-1 ring-line ring-inset">
          <TriangleAlert size={16} className="mt-0.5 shrink-0 text-muted" />
          <span>
            Мало данных {OWNER[post.platform] ?? 'у автора'} — цифре можно верить меньше
            {post.median_samples !== null && (
              <span className="text-muted">
                {' '}
                (обычное считается по {post.median_samples}{' '}
                {plural(post.median_samples, 'посту', 'постам', 'постам')})
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  )
}

interface Row {
  icon: LucideIcon
  label: string
  value: number | null
  usual?: number
  share?: { value?: number; of: string; usual?: number }
  note?: string
  state?: MetricState
}

/** Что нужно списку метрик: карточке поста и цифрам своей публикации в контент-плане. */
export type MetricsSource = Pick<PostDetail, 'platform' | 'kind' | 'metrics' | 'usual' | 'shares' | 'usual_shares' | 'is_early'> & Measurement

function rows(post: MetricsSource): Row[] {
  const m = post.metrics
  const u = post.usual
  const s = post.shares
  const us = post.usual_shares
  const provenance = (key: 'views' | 'plays' | 'likes' | 'comments') => {
    const info = post.metric_sources?.[key]
    if (!info) return {}
    const provider = info.provider === 'meta_bd' ? 'Meta' : info.provider === 'apify' ? 'Apify' : info.provider
    return { state: info.state, note: `${provider}${info.observed_at ? ` · ${formatDateTime(info.observed_at)}` : ''}${info.is_stale ? ' · устарело' : ''}` }
  }
  const share = (key: string, of: string) =>
    s[key] !== undefined || us[key] !== undefined ? { value: s[key], of, usual: us[key] } : undefined

  if (post.platform === 'telegram') {
    return [
      { icon: Eye, label: 'Просмотры', value: m.plays, usual: u.plays, share: share('plays_per_follower', 'подписчиков') },
      { icon: Flame, label: 'Реакции', value: m.reactions, usual: u.reactions, share: share('reactions_per_play', 'просмотров') },
      { icon: MessageCircle, label: 'Комментарии', value: m.comments, usual: u.comments, share: share('comments_per_play', 'просмотров') },
      { icon: Forward, label: 'Пересылки', value: m.forwards, usual: u.forwards },
    ]
  }
  if (post.platform === 'reddit') {
    const per = s.comments_per_score
    const usualPer = us.comments_per_score
    const fmt = (v: number) => v.toFixed(2).replace('.', ',')
    return [
      {
        icon: ArrowBigUp,
        label: 'Очки',
        value: m.likes,
        usual: u.likes,
        note: m.upvote_ratio !== null ? `${formatPct(m.upvote_ratio)} апвоутов` : undefined,
      },
      {
        icon: MessageCircle,
        label: 'Комментарии',
        value: m.comments,
        usual: u.comments,
        note:
          per !== undefined
            ? `${fmt(per)} на одно очко${usualPer !== undefined ? ` · обычно ${fmt(usualPer)}` : ''}`
            : undefined,
      },
    ]
  }
  if (post.kind === 'reel') {
    return [
      { icon: Eye, label: viewsLabel(post), value: primaryViews(post), usual: isMeta(post) ? u.views : u.plays, share: share(isMeta(post) ? 'views_per_follower' : 'plays_per_follower', 'подписчиков'), ...provenance(isMeta(post) ? 'views' : 'plays') },
      { icon: Heart, label: 'Лайки', value: m.likes, usual: u.likes, share: share(isMeta(post) ? 'likes_per_view' : 'likes_per_play', 'просмотров'), ...provenance('likes') },
      { icon: MessageCircle, label: 'Комментарии', value: m.comments, usual: u.comments, share: share(isMeta(post) ? 'comments_per_view' : 'comments_per_play', 'просмотров'), ...provenance('comments') },
    ]
  }
  return [
    { icon: Heart, label: 'Лайки', value: m.likes, usual: u.likes, share: share('likes_per_follower', 'подписчиков'), ...provenance('likes') },
    { icon: MessageCircle, label: 'Комментарии', value: m.comments, usual: u.comments, share: share('comments_per_follower', 'подписчиков'), ...provenance('comments') },
  ]
}

function PostMetricSummary({ post }: { post: PostDetail }) {
  const available = rows(post).filter((row) => row.value !== null)
  if (available.length === 0) return null

  return (
    <dl aria-label="Основные показатели" className="grid grid-flow-col auto-cols-fr gap-2 border-y border-line py-2.5">
      {available.map(({ label, value }) => (
        <div key={label} className="flex min-w-0 flex-col-reverse">
          <dt className="text-[11px] leading-tight text-muted wrap-anywhere">{label}</dt>
          <dd className="text-lg leading-snug font-semibold text-link tabular-nums">{formatCount(value)}</dd>
        </div>
      ))}
    </dl>
  )
}

export function MetricsList({ post }: { post: MetricsSource }) {
  const list = rows(post).filter((r) => r.value !== null || r.usual !== undefined || isMeta(post) || r.state !== undefined)
  if (list.length === 0) return null
  return (
    <section>
      <ul className="divide-y divide-line rounded-xl ring-1 ring-line ring-inset">
        {list.map(({ icon: Icon, label, value, usual, share, note, state }) => (
          <li key={label} className="px-3.5 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="flex items-center gap-2 text-sm">
                <Icon size={15} className="text-muted" />
                {label}
              </span>
              <span className="text-base font-semibold tabular-nums">{value === null && state ? metricStateLabel(state) : formatCount(value)}</span>
            </div>
            {(usual !== undefined || share || note) && (
              <div className="mt-0.5 flex flex-col gap-0.5 pl-[23px] text-xs text-muted tabular-nums">
                {usual !== undefined && (
                  <span>
                    обычно {OWNER[post.platform] ?? 'у автора'} {formatCount(usual)}
                  </span>
                )}
                {share && (
                  <span>
                    {share.value !== undefined ? `${formatPct(share.value)} ${share.of}` : `доля от ${share.of}`}
                    {share.usual !== undefined && ` · обычно ${formatPct(share.usual)}`}
                  </span>
                )}
                {note && <span>{note}</span>}
              </div>
            )}
          </li>
        ))}
      </ul>
      {post.is_early && !isMeta(post) && (
        <p className="mt-1.5 text-xs text-muted">Доли молодого поста поправлены на дозревание, цифры — последний замер.</p>
      )}
    </section>
  )
}

function SoundLine({ post }: { post: PostDetail }) {
  const s = post.sound
  if (!s) return null
  const name = [s.title, s.artist].filter(Boolean).join(' — ') || 'без названия'
  const tail = s.posts_count
    ? ` · ${s.posts_count} ${plural(s.posts_count, 'ролик', 'ролика', 'роликов')} из ${s.distinct_sources ?? 0} ${plural(
        s.distinct_sources ?? 0,
        'канала',
        'каналов',
        'каналов',
      )} за неделю${s.is_rising ? ', растёт' : ''}`
    : ''
  return (
    <p className="flex items-start gap-2 text-sm">
      <AudioLines size={16} className="mt-0.5 shrink-0 text-muted" />
      <span>
        Звук: {name}
        <span className="text-muted">{tail}</span>
      </span>
    </p>
  )
}

/** Другая публикация сохраняет серию, но не наследует контекст исходного автора. */
export function relatedPostHref(id: number, params: URLSearchParams, resolvedView?: MetricView) {
  return postHref({ id }, { metric_view: postMeasurement(params).metric_view ?? resolvedView })
}

function RepostBlock({ post }: { post: PostDetail }) {
  const r = post.repost
  const location = useLocation()
  const [params] = useSearchParams()
  const [adding, setAdding] = useViewState(`post.${post.id}.addingSource`, false)
  // Канал-оригинал скорее всего из той же ниши, что и канал, который его репостнул.
  const sources = useSources()
  const reposter = sources.data?.find((s) => s.id === post.author.source_id)
  if (!r) return null
  const origin = r.origin_handle ? `@${r.origin_handle}` : 'закрытого канала'
  return (
    <div className="flex flex-col gap-1.5 rounded-xl bg-surface px-3.5 py-2.5 text-sm ring-1 ring-line ring-inset">
      <div className="flex items-center gap-2 font-medium">
        <Repeat2 size={16} className="shrink-0 text-muted" />
        <span className="min-w-0">
          Репост из {origin}
          {!r.origin_tracked && <span className="font-normal text-muted"> · не отслеживается</span>}
        </span>
      </div>
      <p className="text-muted">
        {r.metrics_from === 'origin'
          ? 'Цифры и ×N — у оригинала в его канале.'
          : `Цифры — у репоста в @${post.author.handle}.`}
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {r.origin_url && (
          <button type="button" className="text-link hover:underline" onClick={() => openExternal(r.origin_url ?? '')}>
            Оригинал в Telegram
          </button>
        )}
        {r.origin_post_id !== null && (
          <Link
            to={relatedPostHref(r.origin_post_id, params, post.metric_view)}
            state={location.state}
            replace
            className="text-link hover:underline"
          >
            Карточка оригинала
          </Link>
        )}
        {post.url && (
          <button type="button" className="text-link hover:underline" onClick={() => openExternal(post.url ?? '')}>
            Репост в @{post.author.handle}
          </button>
        )}
      </div>
      {!r.origin_tracked && r.origin_handle && (
        <Button size="sm" className="mt-1 self-start" onClick={() => setAdding(true)}>
          <Plus size={14} />
          Добавить @{r.origin_handle} в источники
        </Button>
      )}
      {adding && r.origin_handle && (
        <AddSourceDialog
          initialInput={sourceInput('telegram', r.origin_handle)}
          initialCollectionIds={reposter?.collection_ids ?? []}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  )
}

function Linked({ title, posts, view }: { title: string; posts: LinkedPost[]; view?: MetricView }) {
  const location = useLocation()
  const [params] = useSearchParams()
  return (
    <section>
      <h3 className="mb-1.5 text-xs font-medium tracking-wide text-muted uppercase">{title}</h3>
      <ul className="flex flex-wrap gap-1.5">
        {posts.map((p) => (
          <li key={p.id}>
            <Link
              to={relatedPostHref(p.id, params, view)}
              state={location.state}
              replace
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-surface px-2.5 text-sm ring-1 ring-line ring-inset hover:bg-line/50"
            >
              {handleLabel(p.platform, p.handle)}
              {p.xn !== null && <span className="text-muted tabular-nums">{formatX(p.xn)}</span>}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}


type Tab = 'transcript' | 'caption' | 'storyboard'

function Tabs({ post, videoRef }: { post: PostDetail; videoRef: RefObject<HTMLVideoElement | null> }) {
  const [tab, setTab] = useViewState<Tab>(`post.${post.id}.tab`, post.kind === 'reel' || post.transcript ? 'transcript' : 'caption')

  const tabs: { id: Tab; label: string; soon?: boolean }[] = [
    { id: 'transcript', label: 'Транскрипт' },
    { id: 'caption', label: 'Подпись' },
    { id: 'storyboard', label: 'Раскадровка' },
  ]

  return (
    <section>
      <div role="tablist" className="mb-3 flex gap-1 border-b border-line">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px inline-flex h-10 items-center gap-1.5 border-b-2 px-2.5 text-sm transition-colors desk:h-9 ${
              tab === t.id ? 'border-fg font-medium text-fg' : 'border-transparent text-muted hover:text-fg'
            }`}
          >
            {t.label}
            {t.soon && <span className="rounded bg-surface px-1 py-0.5 text-[10px] leading-none text-muted">скоро</span>}
          </button>
        ))}
      </div>
      {tab === 'transcript' && <ReelTranscript key={post.id} post={post} videoRef={videoRef} />}
      {tab === 'caption' &&
        (post.caption ? (
          <CopyableText text={post.caption} />
        ) : (
          <p className="text-sm text-muted">Подписи нет.</p>
        ))}
      {tab === 'storyboard' && <Storyboard post={post} videoRef={videoRef} />}
    </section>
  )
}

function TextBlock({ title, text }: { title: string; text: string | null }) {
  return (
    <section>
      <h3 className="mb-1.5 text-xs font-medium tracking-wide text-muted uppercase">{title}</h3>
      {text ? <CopyableText text={text} /> : <p className="text-sm text-muted">Без текста.</p>}
    </section>
  )
}

function CopyableText({ text }: { text: string }) {
  return (
    <div>
      <CopyButton text={text} />
      <p className="mt-2 text-[15px] leading-relaxed break-words whitespace-pre-line desk:text-sm">{text}</p>
    </div>
  )
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false)
  useEffect(() => {
    if (!done) return
    const t = setTimeout(() => setDone(false), 1500)
    return () => clearTimeout(t)
  }, [done])
  return (
    <Button size="sm" onClick={async () => setDone(await copyOrShow(text))}>
      {done ? <Check size={14} /> : <Copy size={14} />}
      {done ? 'Скопировано' : 'Копировать'}
    </Button>
  )
}
